import React, {useEffect, useState, useRef, ChangeEvent} from "react";
import { useNavigate } from "react-router-dom";
import InterfaceContactInfo from "../../interface/InterfaceContactInfo";
import InterfaceBooking from "../../interface/InterfaceBooking";
import {
  isValidEmail,
  isValidPhoneNumber,
  trimPhoneNumber
} from "../../helpers/InputValidators";
import {useApptekster} from "../../apptekst-context";
import {
  SVVRadioGroup, 
  SVVCheckboxGroup,
  SVVErrorSummary,
  SVVTextInput
} from "@komponentkassen/svv-form";
import {isReminderString, ReminderString} from "../../helpers/SharedTypes";
import CustomSVVTextInput from "../../components/CustomSVVTextInput";
import FormButtons from "../../components/FormButtons";
import {SVVBodyText, SVVHeading} from "@komponentkassen/svv-text";

type InputObjectType = { 
  id: string
  label: string
  errorMessage: string
}

const ContactInfo = (props: {
  originalBooking: InterfaceBooking;
  newBooking?: InterfaceBooking;
  setContactInfo: any;
  contactInfo: InterfaceContactInfo;
  setNavState: any;
  abort: any;
  setSendReminderTo: (val: ReminderString) => void;
  backUrl: string
  avbrytUrl?: string
}) => {
  const t = useApptekster();

  const skjemaInputFelter = {
    firstName: {
      id: "fornavn",
      label: t("timebestilling.fornavn"),
      errorMessage: t("timebestilling.fyll-inn-fornavn")
    },
    lastName: {
      id: "etternavn",
      label: t("timebestilling.etternavn"),
      errorMessage: t("timebestilling.fyll-inn-etternavn")
    },
    emailAddress: {
      id: "emailAddress",
      label: t("timebestilling.epostadr"),
      errorMessage: t("timebestilling.fyll-inn-epost")
    },
    phoneNumber: {
      id: "mobil",
      label: t("timebestilling.mobilnummer"),
      errorMessage: t("timebestilling.fyll-inn-mobil"),
      helpText: t("timebestilling.mobilnummer-hjelpetekst")
    },
    hasConsented: {
      id: "samtykke",
      label: t("timebestilling.jeg-samtykker"),
      errorMessage: t("timebestilling.du-ma-samtykke")
    }
  };

  const CONTACT_INFO = {
    firstName: props.originalBooking?.firstName ? props.originalBooking?.firstName : '',
    lastName: props.originalBooking?.lastName ? props.originalBooking?.lastName : '',
    emailAddress: props.originalBooking?.emailAddress ? props.originalBooking?.emailAddress : '',
    phoneNumber: props.originalBooking?.telephoneNumber ? props.originalBooking?.telephoneNumber : '',
    sendConfirmationSms: false,
    sendReminderEmail: true,
    sendReminderSms: true,
    hasConsented: false
  };

  const [formContactInfo, setFormContactInfo] = useState<InterfaceContactInfo>(CONTACT_INFO);
  const [invalidInputs, setInvalidInputs] = useState<InputObjectType[]>([]);
  const [tryToSubmit, setTryToSubmit] = useState<boolean>(false);
  const [consentedValid, setConsentedValid] = useState<boolean>(false);
  const [sendReminderTo, setSendReminderTo] = useState<ReminderString>("both")
  
  const navigate = useNavigate();
  
  const headingRef = useRef<HTMLHeadingElement | null>(null)

  const removeFromInvalidInputs = (propertyToRemove: string) => {
    setInvalidInputs(invalidInputs.filter(i => i.id !== propertyToRemove ));
  }

  useEffect(() => {
    if (headingRef?.current) {
      headingRef.current.focus()
    }
  }, [headingRef])

  useEffect(() => {
      if (formContactInfo.firstName && props.contactInfo.firstName === '' && props.originalBooking.firstName) {
        const postToUpdate: InterfaceContactInfo = {
          ...formContactInfo,
          firstName: props.originalBooking.firstName,
          lastName: props.originalBooking.lastName,
          emailAddress: props.originalBooking.emailAddress,
          phoneNumber: props.originalBooking.telephoneNumber
        };
        setFormContactInfo(postToUpdate);
        props.setContactInfo(postToUpdate);
      }

      if (props.contactInfo) {
        setFormContactInfo(props.contactInfo);
        const {
          sendReminderSms,
          sendReminderEmail
        } = props.contactInfo
        setSendReminderTo((sendReminderSms && sendReminderEmail) ? "both" : sendReminderSms ? "sms" : "email") 
      }
    },
    // Eslint vil ha formContactInfo og props i dependencies, men det trengs ikke
    // eslint-disable-next-line
    [props.contactInfo]
  );

  function toggleConsented() {
    consentedValid ? setConsentedValid(false) : setConsentedValid(true);

    const postToUpdate = {
      ...formContactInfo,
      hasConsented: !formContactInfo.hasConsented
    };
    setFormContactInfo(postToUpdate);
  }

  const firstNameChange = (firstName: string) => {
    if (tryToSubmit) {
      removeFromInvalidInputs(skjemaInputFelter.firstName.id);
    }
    const postToUpdate = {
      ...formContactInfo,
      firstName: firstName
    };
    setFormContactInfo(postToUpdate);
  }

  const lastNameChange = (lastName: string) => {
    if (tryToSubmit) {
      removeFromInvalidInputs(skjemaInputFelter.lastName.id);
    }
    const postToUpdate = {
      ...formContactInfo,
      lastName: lastName
    };
    setFormContactInfo(postToUpdate);
  }

  const emailAddressChange = (emailAddress: string) => {
    if (tryToSubmit) {
      removeFromInvalidInputs(skjemaInputFelter.emailAddress.id);
    }
    const postToUpdate = {
      ...formContactInfo,
      emailAddress: emailAddress
    };
    setFormContactInfo(postToUpdate);
  }
  const phoneNumberChange = (phoneNumber: string) => {
    if (tryToSubmit) {
      removeFromInvalidInputs(skjemaInputFelter.phoneNumber.id);
    }
    const postToUpdate = {
      ...formContactInfo,
      phoneNumber: phoneNumber
    };
    setFormContactInfo(postToUpdate);
  }

  const inputHasError = (skjemaInputFelt: InputObjectType): string => {
    return (tryToSubmit && invalidInputs.some(i => i.id === skjemaInputFelt.id)) ? skjemaInputFelt.errorMessage : "";
  };
  
  const removeInputFromInvalidInputs = (inputToRemove: InputObjectType) => {
    return invalidInputs.filter(x => x.id !== inputToRemove.id)
  }
  
  const reminderChange = (val: ReminderString) => {
    //Oppdater hvor påminnelse skal sendes når man endrer valg i radioknappen
    const postToUpdate = {
      ...formContactInfo,
      sendReminderEmail: (val === "both" || val === "email"),
      sendReminderSms: (val === "both" || val === "sms"),
      sendConfirmationSms: (val === "both" || val === "sms")
    }
    
    //Når radioknappen står på valget "both" er både epostadresse og telefonnummer
    //påkrevde felt. Hvis man trykker på "Neste" uten å ha fylt inn en eller begge 
    //havner begge feltene i state-variabelen invalidInputs. Endrer man fra "both"
    //til "sms" eller "email" må det motsatte valget fjernes fra invalidInputs, fordi 
    //har man valgt "sms" skal ikke epost-feltet lenger være påkrevd å fylle ut.
    if (tryToSubmit) {
      if (val === "sms" && inputHasError(skjemaInputFelter.emailAddress)) {
        setInvalidInputs(removeInputFromInvalidInputs(skjemaInputFelter.emailAddress))
      }
      if (val === "email" && inputHasError(skjemaInputFelter.phoneNumber)) {
        setInvalidInputs(removeInputFromInvalidInputs(skjemaInputFelter.phoneNumber))
      }
    }
    
    setSendReminderTo(val)
    setFormContactInfo(postToUpdate);
  }

  const validateAll = () => {
    let invalids = [];

    if (!formContactInfo.firstName) {
      invalids.push(skjemaInputFelter.firstName);
    }
    if (!formContactInfo.lastName) {
      invalids.push(skjemaInputFelter.lastName);
    }
    if (!isValidEmail(formContactInfo.emailAddress) && (sendReminderTo === "both" || sendReminderTo === "email")) {
      invalids.push(skjemaInputFelter.emailAddress);
    }
    if (!isValidPhoneNumber(trimPhoneNumber(formContactInfo.phoneNumber)) && (sendReminderTo === "both" || sendReminderTo === "sms")) {
      invalids.push(skjemaInputFelter.phoneNumber);
    }
    if (!formContactInfo.hasConsented) {
      invalids.push(skjemaInputFelter.hasConsented);
    }
    
    setInvalidInputs(invalids);
    return invalids.length === 0;
  }

  const submit = (event: any) => {
    event.preventDefault();
    setTryToSubmit(true);

    if (validateAll()) {
      props.setContactInfo({
          ...formContactInfo,
          phoneNumber: trimPhoneNumber(formContactInfo.phoneNumber)
      });
      props.setSendReminderTo(sendReminderTo);
      props.setNavState();
      const url: string = props.originalBooking?.bookingNumber ? '/endre/bekreftetime' : '/bestill/bekrefte'
      navigate(url);
    }
  };

  return (
    <>
      <SVVHeading
        level="h2"
        ref={headingRef}
        tabIndex={-1}
      >
        {t("timebestilling.kontaktinformasjon")}
      </SVVHeading>
      <SVVBodyText className="svv-padding-bottom--px24">
        {t("timebestilling.fyll-inn-kontaktinfo")}
      </SVVBodyText>
      
      <form 
        onSubmit={submit} 
        noValidate
        className="svv-padding-bottom-tablet-down--px32 svv-padding-bottom-tablet-up--px8"
      >
        <div className="form-control">
          <div className="spacing-bottom--small">
            <SVVTextInput 
              label={skjemaInputFelter.firstName.label} 
              id={skjemaInputFelter.firstName.id}
              errorMessage={inputHasError(skjemaInputFelter.firstName)}
              onChange={e => firstNameChange(e.target.value)}
              value={formContactInfo.firstName}
              inputSize="large"
              autoComplete="given-name"
              required
            />
          </div>
          <div className="spacing-bottom--small">
            <SVVTextInput
              label={skjemaInputFelter.lastName.label}
              id={skjemaInputFelter.lastName.id}
              value={formContactInfo.lastName}
              onChange={e => lastNameChange(e.target.value)}
              errorMessage={inputHasError(skjemaInputFelter.lastName)}
              autoComplete="family-name"
              inputSize="large"
              required
            />
          </div>
        </div>
        
        <SVVHeading 
          level="h3" 
          className="spacing-top--large spacing-bottom--xsmall"
        >
          {t("timebestilling.bekreftelse")}
        </SVVHeading>
        
        <SVVRadioGroup
          id="send-reminder"
          legend={t("timebestilling.tilsendt-bekreftelse")}
          required
          name="send-reminder"
          selected={sendReminderTo}
          options={[
            {
              value: "both",
              text: t("timebestilling.send-paminnelse-velg-begge")
            },
            {
              value: "sms",
              text: t("timebestilling.send-paminnelse-velg-sms")
            },
            
            {
              value: "email",
              text: t("timebestilling.send-paminnelse-velg-epost")
            },
          ]}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            if (isReminderString(e.target.value)) {
              reminderChange(e.target.value)
            }
          }}
        />
        
        {(sendReminderTo === "both" || sendReminderTo === "sms") &&
          <div className="spacing-bottom--small">
            <CustomSVVTextInput
              label={skjemaInputFelter.phoneNumber.label}
              id={skjemaInputFelter.phoneNumber.id}
              value={formContactInfo.phoneNumber}
              onChange={e => phoneNumberChange(e.target.value)}
              errorMessage={inputHasError(skjemaInputFelter.phoneNumber)}
              inputSize="large"
              autoComplete="tel"
              type="tel"
              required
              countryCode="+47"
              helpText={skjemaInputFelter.phoneNumber.helpText}
            />
          </div>
        }
        
        {(sendReminderTo === "both" || sendReminderTo === "email") &&
          <div className="spacing-bottom--small">
            <SVVTextInput
              label={skjemaInputFelter.emailAddress.label}
              id={skjemaInputFelter.emailAddress.id}
              value={formContactInfo.emailAddress}
              onChange={e => emailAddressChange(e.target.value)}
              errorMessage={inputHasError(skjemaInputFelter.emailAddress)}
              inputSize="large"
              autoComplete="email"
              type="email"
              required
            />
          </div>
        }
        
        <SVVHeading level="h3">
          {t("timebestilling.paminnelse")}
        </SVVHeading>
        <SVVBodyText marginBottom="lg">
          {t("timebestilling.paminnelse-24")}
        </SVVBodyText>
        <SVVHeading level="h3">
          {t("timebestilling.samtykke-lagring")}
        </SVVHeading>
        <SVVBodyText marginBottom="xs">
          {t("timebestilling.bruker-informasjonen")}
        </SVVBodyText>
        
        <SVVCheckboxGroup 
          legend={t("timebestilling.samtykke-lagre-personopplysninger")} 
          options={[
            {
              id: skjemaInputFelter.hasConsented.id,
              label: skjemaInputFelter.hasConsented.label,
              onChange: toggleConsented,
              checked: formContactInfo.hasConsented
            }
          ]}
          error={inputHasError(skjemaInputFelter.hasConsented)}
          required
        />

        {invalidInputs.length > 0 &&
          <div className="error-visible spacing-vertical--medium" aria-live="polite">
            <SVVErrorSummary
              label={t("timebestilling.for-a-ga-videre")}
              errorMessages={invalidInputs.map((i) => {
                return {
                  id: i.id,
                  errorMessage: i.errorMessage,
                };
              })}
            />
          </div>
        }
        <FormButtons 
          submitText={t("timebestilling.neste")}
          backText={t("timebestilling.tilbake")}
          cancelText={t("timebestilling.avbryt")} 
          backUrl={props.backUrl} 
          onCancel={props.abort}
          avbrytUrl={props.avbrytUrl}
        />
      </form>
    </>
  );
};

export default ContactInfo;